import { HTMLAttributes, forwardRef } from 'react'
import { clsx } from 'clsx'

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: 'success' | 'warning' | 'danger' | 'info' | 'gray' | 'primary'
  size?: 'sm' | 'md' | 'lg'
  dot?: boolean
}

const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant = 'gray', size = 'md', dot = false, children, ...props }, ref) => {
    const variants = {
      success: 'badge badge-success',
      warning: 'badge badge-warning',
      danger: 'badge badge-danger',
      info: 'badge badge-info',
      gray: 'badge badge-gray',
      primary: 'badge badge-info'
    }

    const sizes = {
      sm: 'text-xs px-2 py-0.5',
      md: 'text-sm px-3 py-1',
      lg: 'text-base px-4 py-1.5'
    }

    if (dot) {
      return (
        <span
          ref={ref}
          className={clsx(
            'inline-flex items-center gap-1.5',
            sizes[size],
            className
          )}
          {...props}
        >
          <span className={clsx(
            'w-2 h-2 rounded-full',
            variant === 'success' && 'bg-success-500',
            variant === 'warning' && 'bg-warning-500',
            variant === 'danger' && 'bg-danger-500',
            variant === 'info' && 'bg-primary-500',
            variant === 'gray' && 'bg-secondary-400',
            variant === 'primary' && 'bg-primary-500'
          )} />
          {children}
        </span>
      )
    }

    return (
      <span
        ref={ref}
        className={clsx(
          variants[variant],
          sizes[size],
          className
        )}
        {...props}
      >
        {children}
      </span>
    )
  }
)

Badge.displayName = 'Badge'

export default Badge
